/**
 * Module #34 Phase A chunk 2 — conversation-list hook for the sidebar.
 *
 * Wraps GET /v1/copilot/conversations + POST /v1/copilot/conversations/:id/archive.
 * Returns a reactive list of the signed-in user's own conversations.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSession } from './useSession';
import { fetchWithAuth } from '../lib/accessToken';

export interface CopilotConversationListItem {
  conversationId: string;
  title: string | null;
  summary: string | null;
  messageCount: number;
  lastMessageAt: string | null;
  createdAt: string;
}

export type ConversationListStatus =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; detail: string };

export interface UseCopilotConversationsReturn {
  conversations: CopilotConversationListItem[];
  status: ConversationListStatus;
  /**
   * Re-fetch the list. Pass `{ silent: true }` for a background refresh that
   * doesn't flip the sidebar to a "Loading…" state — used after a new chat's
   * first turn so the row (with its auto-title) slides in without a flash.
   */
  refresh: (opts?: { silent?: boolean }) => Promise<void>;
  archive: (id: string) => Promise<boolean>;
  /** Rename a conversation; updates the local list on success. */
  rename: (id: string, title: string) => Promise<boolean>;
}

interface ListApiResponse {
  conversations: Array<{
    conversation_id: string;
    title: string | null;
    summary: string | null;
    message_count: number;
    last_message_at: string | null;
    created_at: string;
  }>;
  count: number;
}

interface ApiError {
  error: string;
  detail: string;
}

function ieBaseUrl(): string | null {
  const url = import.meta.env.VITE_INTELLIGENCE_BASE_URL as string | undefined;
  return url && url.length > 0 ? url.replace(/\/$/, '') : null;
}

export function useCopilotConversations(): UseCopilotConversationsReturn {
  const { userId } = useSession();
  const base = useMemo(() => ieBaseUrl(), []);
  const [conversations, setConversations] = useState<CopilotConversationListItem[]>([]);
  const [status, setStatus] = useState<ConversationListStatus>({ kind: 'idle' });

  const refresh = useCallback(
    async (opts?: { silent?: boolean }): Promise<void> => {
      if (base === null || userId === null) {
        setStatus({ kind: 'idle' });
        setConversations([]);
        return;
      }
      if (opts?.silent !== true) setStatus({ kind: 'loading' });
      let res: Response;
      try {
        res = await fetchWithAuth(`${base}/v1/copilot/conversations?limit=50`);
      } catch (err) {
        setStatus({
          kind: 'error',
          detail: err instanceof Error ? err.message : 'Network error',
        });
        return;
      }
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as ApiError | null;
        setStatus({ kind: 'error', detail: body?.detail ?? `HTTP ${res.status}` });
        return;
      }
      const data = (await res.json()) as ListApiResponse;
      setConversations(
        data.conversations.map((c) => ({
          conversationId: c.conversation_id,
          title: c.title,
          summary: c.summary,
          messageCount: c.message_count,
          lastMessageAt: c.last_message_at,
          createdAt: c.created_at,
        })),
      );
      setStatus({ kind: 'idle' });
    },
    [base, userId],
  );

  // Auto-load on mount + when the session/base flips.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const archive = useCallback(
    async (id: string): Promise<boolean> => {
      if (base === null || userId === null) return false;
      let res: Response;
      try {
        res = await fetchWithAuth(
          `${base}/v1/copilot/conversations/${encodeURIComponent(id)}/archive`,
          {
            method: 'POST',
          },
        );
      } catch {
        return false;
      }
      if (!res.ok) return false;
      // Optimistic: remove from local list.
      setConversations((prev) => prev.filter((c) => c.conversationId !== id));
      return true;
    },
    [base, userId],
  );

  const rename = useCallback(
    async (id: string, title: string): Promise<boolean> => {
      if (base === null || userId === null) return false;
      const clean = title.replace(/\s+/g, ' ').trim();
      if (clean.length === 0) return false;
      let res: Response;
      try {
        res = await fetchWithAuth(
          `${base}/v1/copilot/conversations/${encodeURIComponent(id)}/rename`,
          {
            method: 'POST',
            headers: {
              'content-type': 'application/json',
            },
            body: JSON.stringify({ title: clean }),
          },
        );
      } catch {
        return false;
      }
      if (!res.ok) return false;
      // Use the server's canonical title (it trims/caps); fall back to ours.
      const body = (await res.json().catch(() => null)) as { title?: string } | null;
      const finalTitle = typeof body?.title === 'string' ? body.title : clean;
      setConversations((prev) =>
        prev.map((c) => (c.conversationId === id ? { ...c, title: finalTitle } : c)),
      );
      return true;
    },
    [base, userId],
  );

  return { conversations, status, refresh, archive, rename };
}
